export interface DmNotice {
  dm_xid: string;
  message_xid: string;
  author_xid: string;
  created_at: string;
  muted: boolean;
}

export function parseDmNotice(value: unknown): DmNotice | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (![v.dm_xid, v.message_xid, v.author_xid, v.created_at].every(x => typeof x === "string" && x.length > 0)
    || typeof v.muted !== "boolean" || !Number.isFinite(Date.parse(v.created_at as string))) return null;
  return v as unknown as DmNotice;
}

export function isConversationVisible(active: string | null, dm: string, visible: boolean, focused: boolean): boolean {
  return active === dm && visible && focused;
}

export function shouldChime(notice: DmNotice, context: {
  account: string; active: string | null; visible: boolean; focused: boolean;
  enabled: boolean; busy: boolean; deafened: boolean; replayed: boolean; now: number | null;
}): boolean {
  // Wait for the connection's clock sample; never guess using the device clock.
  if (context.now === null) return false;
  const age = context.now - Date.parse(notice.created_at);
  return context.enabled && !context.busy && !context.deafened && !context.replayed
    && !notice.muted && notice.author_xid !== context.account
    && age >= -5_000 && age <= 15_000
    && !isConversationVisible(context.active, notice.dm_xid, context.visible, context.focused);
}

export const DM_SOUND_KEY = "link.notifications.dm-sound";
export function dmSoundEnabled(): boolean {
  try { return localStorage.getItem(DM_SOUND_KEY) !== "off"; } catch { return true; }
}
export function setDmSoundEnabled(enabled: boolean): void {
  try { localStorage.setItem(DM_SOUND_KEY, enabled ? "on" : "off"); } catch { /* session-only preference */ }
  sessionEnabled = enabled;
  window.dispatchEvent(new Event("link:notification-settings"));
}
let sessionEnabled: boolean | undefined;
export function currentDmSoundEnabled(): boolean { return sessionEnabled ?? dmSoundEnabled(); }

// An original, quiet two-note chime, generated locally. No audio download,
// microphone permission, recording, or message content is involved.
let audio: AudioContext | null = null;
let lastChime = -Infinity;
const heard = new Set<string>();
export function unlockDmAudio(): void {
  try {
    audio ??= new AudioContext();
    if (audio.state === "suspended") void audio.resume().catch(() => undefined);
  } catch { /* browser autoplay/audio policy: visual unread indicators still work */ }
}
export function installDmAudio(): () => void {
  const unlock = () => { if (currentDmSoundEnabled()) unlockDmAudio(); };
  const sync = (event: StorageEvent) => { if (event.key === DM_SOUND_KEY) sessionEnabled = undefined; };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
  window.addEventListener("storage", sync);
  return () => {
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("storage", sync);
    void audio?.close().catch(() => undefined); audio = null;
    heard.clear(); lastChime = -Infinity;
  };
}

function chime(): boolean {
  if (!audio || audio.state !== "running") return false;
  const start = audio.currentTime;
  [660, 880].forEach((frequency, index) => {
    const tone = audio!.createOscillator();
    const gain = audio!.createGain();
    const at = start + index * 0.09;
    tone.type = "sine"; tone.frequency.value = frequency;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.045, at + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.18);
    tone.connect(gain); gain.connect(audio!.destination);
    tone.onended = () => { tone.disconnect(); gain.disconnect(); };
    tone.start(at); tone.stop(at + 0.2);
  });
  return true;
}

export async function previewDmSound(): Promise<boolean> {
  unlockDmAudio();
  try { await audio?.resume(); return chime(); } catch { return false; }
}

export async function playDmSound(account: string, message: string): Promise<void> {
  const id = `${account}:${message}`;
  if (heard.has(id)) return;
  heard.add(id);
  if (heard.size > 512) heard.delete(heard.values().next().value!);
  // Web Locks + one timestamp claim prevents multiple tabs chiming together.
  // Store no message IDs or content. If storage/locks are unavailable, fall back
  // to per-tab throttling rather than breaking message delivery.
  const play = () => {
    if (!currentDmSoundEnabled() || !audio || audio.state !== "running") return;
    const now = Date.now();
    const monotonicNow = performance.now();
    const key = `link.notifications.last-chime:${account}`;
    let shared = 0;
    try { shared = Number(localStorage.getItem(key)) || 0; } catch { /* storage denied */ }
    const sharedAge = now - shared;
    // A timestamp left in the future by a corrected device clock must not mute
    // notifications for hours. The same-tab burst guard uses elapsed time.
    if (monotonicNow - lastChime < 1_500 || (shared > 0 && sharedAge >= 0 && sharedAge < 1_500)) return;
    try {
      if (!chime()) return;
      lastChime = monotonicNow;
      try { localStorage.setItem(key, String(now)); } catch { /* storage denied */ }
    } catch { /* audio device unavailable */ }
  };
  try {
    if (navigator.locks) await navigator.locks.request("link-dm-chime", play);
    else play();
  } catch { /* tab closed while waiting for the lock */ }
}
